// FILE: src/features/timeSlice.js
// What the server says about the student's time (plan WP-3): today's credited
// time under the 5-hour cap, the 2-hour break, and the enrolment year. Fed by
// GET /api/session/status (SessionGuard), learning-session heartbeats
// (useLearningSession) and /api/progress/summary. The client only displays
// these figures; it never computes them (plan §0 item 5).
import { createSlice } from "@reduxjs/toolkit";

const FIELDS = [
  "dayKey",
  "dailyCreditedSec",
  "dailyRemainingSec",
  "capped",
  "nextDayAt",
  "continuousSec",
  "breakRequired",
  "breakEndsAt",
  "breakRemainingSec",
  "nextDayInSec",
  "onBreak",
  "daysLeft",
  "enrolmentExpiresAt",
  "enrolmentExpired",
  "untimed"
];

const initialState = {
  dayKey: null,
  dailyCreditedSec: null,
  dailyRemainingSec: null,
  capped: false,
  nextDayAt: null,
  continuousSec: null,
  breakRequired: false,
  breakEndsAt: null,
  breakRemainingSec: 0,
  nextDayInSec: null,
  onBreak: false,
  // Local time (Date.now) the last figures arrived: countdowns run from here,
  // so a client clock that differs from the server's doesn't matter.
  receivedAt: 0,
  daysLeft: null,
  enrolmentExpiresAt: null,
  enrolmentExpired: false,
  untimed: false,
  // Which blocking notice is open: "cap" | "break" | null.
  notice: null
};

const timeSlice = createSlice({
  name: "time",
  initialState,
  reducers: {
    timeStatusReceived: {
      reducer(state, { payload, meta }) {
        if (!payload || typeof payload !== "object") return;
        const was = { capped: state.capped, breakRequired: state.breakRequired };
        for (const f of FIELDS) if (payload[f] !== undefined) state[f] = payload[f];
        state.receivedAt = meta.receivedAt;
        // Open a notice when the state starts; close it when the server says it's over.
        if (state.untimed) state.notice = null;
        else if (state.capped && !was.capped) state.notice = "cap";
        else if (state.breakRequired && !was.breakRequired && state.notice !== "cap") state.notice = "break";
        if (state.notice === "cap" && !state.capped) state.notice = null;
        if (state.notice === "break" && !state.breakRequired) state.notice = null;
      },
      prepare: (payload) => ({ payload, meta: { receivedAt: Date.now() } })
    },
    /** A 403 DAILY_LIMIT_REACHED / BREAK_REQUIRED from any time-engine call. */
    timeRefused: {
      reducer(state, { payload, meta }) {
        if (payload?.code === "DAILY_LIMIT_REACHED") {
          state.capped = true;
          state.dailyRemainingSec = 0;
          if (payload.nextDayAt) state.nextDayAt = payload.nextDayAt;
          state.notice = "cap";
        } else if (payload?.code === "BREAK_REQUIRED") {
          state.breakRequired = true;
          if (payload.breakEndsAt) state.breakEndsAt = payload.breakEndsAt;
          if (payload.breakRemainingSec !== undefined) state.breakRemainingSec = payload.breakRemainingSec;
          state.receivedAt = meta.receivedAt;
          if (state.notice !== "cap") state.notice = "break";
        } else if (payload?.code === "COURSE_EXPIRED") {
          state.enrolmentExpired = true;
          state.daysLeft = 0;
        }
      },
      prepare: (payload) => ({ payload, meta: { receivedAt: Date.now() } })
    },
    breakStarted: {
      reducer(state, { payload, meta }) {
        state.onBreak = true;
        if (payload?.breakEndsAt) state.breakEndsAt = payload.breakEndsAt;
        if (payload?.breakRemainingSec !== undefined) {
          state.breakRemainingSec = payload.breakRemainingSec;
          state.receivedAt = meta.receivedAt;
        }
      },
      prepare: (payload) => ({ payload, meta: { receivedAt: Date.now() } })
    },
    /** The break countdown reached zero; the server re-checks on the next start. */
    breakOver(state) {
      state.breakRequired = false;
      state.onBreak = false;
      state.breakEndsAt = null;
      state.continuousSec = 0;
      if (state.notice === "break") state.notice = null;
    },
    enrolmentExpired(state) {
      state.enrolmentExpired = true;
      state.daysLeft = 0;
    },
    dismissNotice(state) {
      state.notice = null;
    }
  }
});

export const { timeStatusReceived, timeRefused, breakStarted, breakOver, enrolmentExpired, dismissNotice } =
  timeSlice.actions;

/** Local ms timestamp at which the break is over (for the countdown). */
export const selectBreakDeadline = (state) =>
  state.time?.breakRequired ? state.time.receivedAt + (state.time.breakRemainingSec || 0) * 1000 : null;

/** Courses can't be opened right now (the dashboard disables Resume). */
export const selectLessonsBlocked = (state) => {
  const t = state.time || initialState;
  if (t.enrolmentExpired) return "expired";
  if (t.untimed) return null;
  if (t.capped) return "cap";
  if (t.breakRequired) return "break";
  return null;
};

export default timeSlice.reducer;
